import React, { useState } from "react";
import { MiddleMileResponse } from "../../../app/models/responseModels/middleMileResponse";
import { Box, Grid, TextField } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { toast } from "react-toastify";
import { UpdateMiddleMileRequest } from "../../../app/models/requestModels/updateMiddleMileRequest";
import { updateMiddleMile } from "../../../app/features/middleMile/middleMileSlice";
import getAxiosError from "../../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../../app/features/login/loginSlice";
import SubmitModalWithEditAndDelete from "../../../components/common/modalPopup/SubmitModalWithEditAndDelete";
import { useSelector } from "react-redux";
import { PAGES } from "../../../enums/PagesEnum";

interface EditMiddleMileModalProps {
  closeEditMiddleMileModal: CallableFunction;
  onCompleted: CallableFunction;
  middleMileRowData: MiddleMileResponse;
}

function UpdateMiddleMileModel(
  middleMileId: string,
  containerReceived: string,
  receivingStarted: string,
  receivingEnded: string,
  notes: string
): UpdateMiddleMileRequest {
  return {
    middleMileId: middleMileId,
    containerReceived: containerReceived,
    receivingStarted: receivingStarted,
    receivingEnded: receivingEnded,
    notes: notes,
  };
}

const EditMiddleMileModal = ({
  closeEditMiddleMileModal,
  onCompleted,
  middleMileRowData,
}: EditMiddleMileModalProps) => {
  const dispatch = useAppDispatch();

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.MIDDLE_MILE
    )
  );

  const IsUpdatePermisssion = pagePermission
    ? pagePermission.isUpdatePermission
    : false;
  const IsDeletePermisssion = pagePermission
    ? pagePermission.isDeletePermission
    : false;

  // ***** States *****
  const [showSubmitButton, setShowSubmitButton] = useState(false);

  const [containerReceivedTime, setContainerReceivedTime] =
    useState<Date | null>(
      middleMileRowData.containerReceived
        ? new Date(new Date(middleMileRowData.containerReceived))
        : null
    );
  const [sortStartTime, setSortStartTime] = useState<Date | null>(
    middleMileRowData.receivingStarted
      ? new Date(new Date(middleMileRowData.receivingStarted))
      : null
  );
  const [sortEndTime, setSortEndTime] = useState<Date | null>(
    middleMileRowData.receivingEnded
      ? new Date(new Date(middleMileRowData.receivingEnded))
      : null
  );

  const [notes, setNotes] = useState(middleMileRowData.notes);

  // ***** Handlers *****
  const handleSubmit = async () => {
    try {
      if (sortStartTime && sortEndTime && sortStartTime > sortEndTime) {
        toast.error("Invalid sort start time and end time");
        return false;
      }
      const model = UpdateMiddleMileModel(
        middleMileRowData.middleMileId,
        containerReceivedTime ? containerReceivedTime.toUTCString() : "",
        sortStartTime ? sortStartTime.toUTCString() : "",
        sortEndTime ? sortEndTime.toUTCString() : "",
        notes
      );
      const response = await dispatch(updateMiddleMile(model)).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, {
          toastId: "updateMiddleMileSuccess",
        });
        onCompleted();
      } else {
        toast.error(response.message!, {
          toastId: "updateMiddleMileError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "updateMiddleMileException",
      });
    }
    return true;
  };

  return (
    <>
      <SubmitModalWithEditAndDelete
        title="Edit Middle Mile Package"
        updateAsyncOperation={handleSubmit}
        closeSubmitModalWithEditAndDelete={closeEditMiddleMileModal}
        size="lg"
        isLoading={false}
        submitButtonName="Save"
        closeModalAfterSubmit={true}
        confirmMessage="Are you sure you want to delete?"
        isUpdatePermission={IsUpdatePermisssion}
        isDeletePermission={IsDeletePermisssion}
        setShowSubmitButton={setShowSubmitButton}
        showSubmitButton={showSubmitButton}
        showDeleteButton={false}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid container rowSpacing={2} columnSpacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                margin="normal"
                id="parcelCenter"
                label="Parcel Center"
                name="parcelCenter"
                size="small"
                fullWidth
                disabled
                value={middleMileRowData.parcelCenterLocationName}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                margin="normal"
                id="client"
                label="Client"
                name="client"
                size="small"
                fullWidth
                disabled
                value={middleMileRowData.clientName}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                margin="normal"
                id="externalPartnerRegion"
                label="External Partner Region"
                name="externalPartnerRegion"
                size="small"
                fullWidth
                disabled
                value={middleMileRowData.externalPartnerRegion}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                margin="normal"
                id="trackingType"
                label="Tracking Type"
                name="trackingType"
                size="small"
                fullWidth
                disabled
                value={middleMileRowData.trackingType}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                margin="normal"
                id="expectedArrival"
                label="Expected Arrival"
                name="expectedArrival"
                size="small"
                fullWidth
                disabled
                value={new Date(
                  middleMileRowData.expectedArrival
                ).toLocaleString("en-US", {
                  hourCycle: "h24",
                })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                margin="normal"
                id="trackingNumber"
                label="Tracking Number"
                name="trackingNumber"
                size="small"
                fullWidth
                disabled
                value={middleMileRowData.trackingNumber}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                margin="normal"
                id="packagesCount"
                label="Packages Count"
                name="packagesCount"
                size="small"
                fullWidth
                disabled
                value={middleMileRowData.packagesCount}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <DateTimePicker
                ampm={false}
                timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                label="Container Received Time"
                value={containerReceivedTime}
                format={"MM/dd/yyyy hh:mm:ss"}
                views={["month", "day", "hours", "minutes", "seconds"]}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    margin: "normal",
                  },
                  popper: {
                    placement: "auto",
                  },
                }}
                onChange={(newValue) => setContainerReceivedTime(newValue)}
                disabled={!showSubmitButton}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <DateTimePicker
                ampm={false}
                timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                label="Sort Start Time"
                value={sortStartTime}
                format={"MM/dd/yyyy hh:mm:ss"}
                views={["month", "day", "hours", "minutes", "seconds"]}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    margin: "normal",
                  },
                  popper: {
                    placement: "auto",
                  },
                }}
                onChange={(newValue) => setSortStartTime(newValue)}
                disabled={!showSubmitButton}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <DateTimePicker
                ampm={false}
                timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                label="Sort End Time"
                value={sortEndTime}
                format={"MM/dd/yyyy hh:mm:ss"}
                views={["month", "day", "hours", "minutes", "seconds"]}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    margin: "normal",
                  },
                  popper: {
                    placement: "auto",
                  },
                }}
                onChange={(newValue) => setSortEndTime(newValue)}
                disabled={!showSubmitButton}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                multiline
                margin="normal"
                id="notes"
                label="Notes"
                name="notes"
                size="small"
                fullWidth
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                disabled={!showSubmitButton}
              />
            </Grid>
          </Grid>
        </Box>
      </SubmitModalWithEditAndDelete>
    </>
  );
};

export default EditMiddleMileModal;
